import React, { useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState("");

  // 2FA state
  const [twofa, setTwofa] = useState({ enabled: false, pending: false });
  const [setupBundle, setSetupBundle] = useState(null);
  const [twofaCode, setTwofaCode] = useState("");
  const [twofaErr, setTwofaErr] = useState("");

  const load = async () => {
    try { const { data } = await adminApi.settings(); setS(data?.settings || {}); } catch {}
    try { const { data } = await adminApi.twofaStatus(); setTwofa(data || {}); } catch {}
  };
  useEffect(()=>{ load(); }, []);
  if (!s) return <div className="admin-page"><div className="oracle-empty" style={{padding:14}}>Loading settings…</div></div>;

  const save = async (patch) => {
    setBusy(true); setSaved("");
    try { const { data } = await adminApi.patchSettings(patch); setSaved("Saved "+new Date().toLocaleTimeString()); setS({...s, ...patch}); }
    finally { setBusy(false); }
  };

  const toggle = (key) => save({ [key]: !s[key] });
  const set = (key, val) => save({ [key]: val });

  return (
    <div className="admin-page" data-testid="admin-settings">
      <div className="admin-page__head">
        <h2 className="admin-page__title">AdminOne Settings</h2>
        {saved && <span className="admin-saved">{saved}</span>}
      </div>
      <div className="admin-settings-grid">
        {[
          ["sports_mode_enabled","Sports Mode"],
          ["oanda_enabled","OANDA Panel"],
          ["pdf_export_enabled","PDF Export"],
          ["chyron_enabled","Chyron Ticker"],
        ].map(([k,label])=>(
          <label key={k} className="admin-toggle">
            <span>{label}</span>
            <input type="checkbox" checked={!!s[k]} onChange={()=>toggle(k)} disabled={busy} data-testid={`set-toggle-${k}`} />
          </label>
        ))}
        <label className="admin-toggle admin-toggle--input">
          <span>HITL Confidence Threshold (0–10)</span>
          <input type="number" min={0} max={10} value={s.hitl_confidence_threshold ?? 8} onChange={(e)=>set("hitl_confidence_threshold", parseInt(e.target.value||"0",10))} disabled={busy} />
        </label>
        <label className="admin-toggle admin-toggle--input">
          <span>Session Retention (days)</span>
          <input type="number" min={1} value={s.session_retention_days ?? 365} onChange={(e)=>set("session_retention_days", parseInt(e.target.value||"1",10))} disabled={busy} />
        </label>
        <label className="admin-toggle admin-toggle--input">
          <span>Audit Retention (days)</span>
          <input type="number" min={1} value={s.audit_retention_days ?? 730} onChange={(e)=>set("audit_retention_days", parseInt(e.target.value||"1",10))} disabled={busy} />
        </label>
        <label className="admin-toggle admin-toggle--input">
          <span>Chyron Speed</span>
          <select value={s.chyron_speed || "normal"} onChange={(e)=>set("chyron_speed", e.target.value)} disabled={busy}>
            <option value="slow">slow</option><option value="normal">normal</option><option value="fast">fast</option>
          </select>
        </label>
      </div>
      <p className="admin-foot-note">Changes are written to the audit log immediately.</p>

      <section className="admin-section" data-testid="admin-2fa-section" style={{ marginTop: 14 }}>
        <h3 className="admin-section__title">Two-Factor Authentication (TOTP)</h3>
        {twofa.enabled ? (
          <div>
            <p style={{ color: "var(--clx-success)", fontFamily: "'Space Mono',monospace", margin: 0 }}>
              ✓ 2FA is currently <b>ENABLED</b> for your AdminOne account.
            </p>
            <div className="admin-filters" style={{ marginTop: 10 }}>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Current 6-digit code"
                value={twofaCode}
                onChange={(e) => setTwofaCode(e.target.value)}
                data-testid="admin-2fa-disable-code"
              />
              <button
                type="button"
                className="oracle-btn oracle-btn--ghost oracle-btn--sm"
                onClick={async () => {
                  setTwofaErr("");
                  try { await adminApi.twofaDisable(twofaCode); setTwofaCode(""); load(); }
                  catch (e) { setTwofaErr(e?.response?.data?.detail || "Failed to disable"); }
                }}
                data-testid="admin-2fa-disable"
              >Disable 2FA</button>
            </div>
            {twofaErr && <div className="oracle-error" style={{ marginTop: 8 }}>{twofaErr}</div>}
          </div>
        ) : setupBundle ? (
          <div>
            <p style={{ marginTop: 0 }}>Scan this URI in your authenticator app (Google Authenticator / 1Password / Authy):</p>
            <textarea readOnly value={setupBundle.otpauth_uri} style={{ width: "100%", minHeight: 70, background: "var(--clx-surface-high)", color: "var(--clx-text-bright)", border: "1px solid var(--clx-border)", borderRadius: 8, padding: 8, fontFamily: "'Space Mono',monospace", fontSize: 11 }} />
            <p style={{ marginTop: 6 }}>Or enter the secret manually: <code style={{ fontFamily: "'Space Mono',monospace" }}>{setupBundle.secret}</code></p>
            <div className="admin-filters" style={{ marginTop: 8 }}>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Enter 6-digit code from app"
                value={twofaCode}
                onChange={(e) => setTwofaCode(e.target.value)}
                data-testid="admin-2fa-verify-code"
              />
              <button
                type="button"
                className="oracle-btn oracle-btn--gold oracle-btn--sm"
                onClick={async () => {
                  setTwofaErr("");
                  try {
                    await adminApi.twofaVerify(twofaCode);
                    setSetupBundle(null); setTwofaCode("");
                    load();
                  } catch (e) {
                    setTwofaErr(e?.response?.data?.detail || "Invalid code");
                  }
                }}
                data-testid="admin-2fa-verify"
              >Verify & Enable</button>
              <button type="button" className="oracle-btn oracle-btn--ghost oracle-btn--sm" onClick={() => { setSetupBundle(null); setTwofaCode(""); }}>Cancel</button>
            </div>
            {twofaErr && <div className="oracle-error" style={{ marginTop: 8 }}>{twofaErr}</div>}
          </div>
        ) : (
          <button
            type="button"
            className="oracle-btn oracle-btn--gold oracle-btn--sm"
            onClick={async () => {
              setTwofaErr("");
              try { const { data } = await adminApi.twofaSetup(); setSetupBundle(data); }
              catch (e) { setTwofaErr(e?.response?.data?.detail || "Setup failed"); }
            }}
            data-testid="admin-2fa-setup"
          >Set up 2FA</button>
        )}
      </section>
    </div>
  );
}
